import {LoaderFunctionArgs, RouteObject, RouterContextProvider, redirect} from 'react-router';

import {EPermission, ERoles, userEndpoints} from 'src/containers/User';
import PATHS from 'src/paths';
import {getDispatch, getState} from 'src/routerContext';

import {paymentTabRoutes} from '../Licenses/PaymentTab/routes';
import {EUserCompanyProfileTabs, TABS} from './definitions';

const checkPaymentAccess = async (context: RouterContextProvider) => {
  const permissions = getState(context).user?.permissions ?? [];
  const roles = getState(context).user?.roles ?? [];

  return (
    permissions.includes(EPermission.PERMISSION_VIEW_BILLING) &&
    permissions.includes(EPermission.PERMISSION_TRANSACTION_READ) &&
    roles.includes(ERoles.ROLE_USER)
  );
};

const checkLicenseAccess = async (context: RouterContextProvider) => {
  const dispatch = getDispatch(context);
  const {data: hasBilling} = await dispatch(userEndpoints.getBillingFlag.initiate());

  return !!hasBilling;
};

const accessHash: Partial<Record<string, (context: RouterContextProvider) => Promise<boolean>>> = {
  [EUserCompanyProfileTabs.PAYMENT]: checkPaymentAccess,
  [EUserCompanyProfileTabs.LICENSE]: checkLicenseAccess,
};

const getMainTab = async (context: RouterContextProvider) => {
  for (const tab of TABS) {
    const checkAccess = accessHash[tab];

    if (!checkAccess) {
      return tab;
    }

    const hasAccess = await checkAccess(context);

    if (hasAccess) {
      return tab;
    }
  }

  return null;
};

const mainTabLoader = async ({context}: LoaderFunctionArgs) => {
  const mainTab = await getMainTab(context);

  throw redirect(mainTab || PATHS.HOME);
};

const toMainTab = async (context: RouterContextProvider) => {
  const mainTab = await getMainTab(context);

  throw redirect(mainTab ? `../${mainTab}` : PATHS.HOME);
};

const children: RouteObject[] = [
  {
    index: true,
    loader: mainTabLoader,
  },
  {
    path: EUserCompanyProfileTabs.PAYMENT,
    loader: async (args) => {
      const hasAccess = await checkPaymentAccess(args.context);

      if (!hasAccess) {
        await toMainTab(args.context);

        return;
      }

      if (typeof paymentTabRoutes.loader === 'function') {
        const data = await paymentTabRoutes.loader(args);

        return data;
      }
    },
    lazy: async () => ({
      Component: (await import('src/containers/Licenses/PaymentTab')).default,
    }),
    children: paymentTabRoutes.children,
  },
  {
    path: EUserCompanyProfileTabs.LICENSE,
    loader: async ({context}) => {
      const hasAccess = await checkLicenseAccess(context);

      if (hasAccess) {
        return null;
      }

      await toMainTab(context);
    },
    lazy: async () => ({
      Component: (await import('src/containers/Licenses/LicensesTab')).default,
    }),
  },
  {
    path: EUserCompanyProfileTabs.REQUISITES,
    lazy: async () => ({
      Component: (await import('src/containers/CompanyDetails')).CompanyDetails,
    }),
  },
  {
    path: EUserCompanyProfileTabs.MAILINGS,
    lazy: async () => ({
      Component: (await import('./MailingsTab')).MailingsTab,
    }),
  },
  {
    path: EUserCompanyProfileTabs.USERS,
    lazy: async () => ({
      Component: (await import('./UsersTab')).UsersTab,
    }),
  },
];

export const userCompanyProfileRoutes: RouteObject = {
  loader: async ({context}) => {
    const availableTabs: EUserCompanyProfileTabs[] = [];

    for (const tab of TABS) {
      const checkAccess = accessHash[tab];

      if (!checkAccess) {
        availableTabs.push(tab);
        continue;
      }

      const hasAccess = await checkAccess(context);

      if (hasAccess) {
        availableTabs.push(tab);
      }
    }

    return {
      tabs: availableTabs,
    };
  },
  children,
};
